import { Outlet } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  LayoutDashboard,
  Users,
  Umbrella,
  CalendarDays,
  GitBranch,
  Settings,
  Mail,
  ScrollText,
  BarChart3,
  RotateCcw,
  FileSearch,
  Receipt,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  Tag,
  Plane,
  ClipboardList,
  Briefcase,
  HelpCircle,
  SlidersHorizontal,
  Wrench,
  CalendarRange,
  Shield,
  Stamp,
} from 'lucide-react';
import Sidebar, { useSidebar } from '../ui/Sidebar';
import { cn } from '@/lib/utils';
import { useCurrentUser } from '../../hooks/useCurrentUser.jsx';
import WhatsNew from '../WhatsNew.jsx';

// Admin navigation. Each item carries the `section` it belongs to (see
// api/shared/permissions.js); the sidebar shows an item only if the user's
// role grants that section (admins see everything). Items with no section
// are always shown to admin-panel users (Help); `adminOnly` items are for
// full admins only (Dashboard fires admin-wide queries).
const NAV_SECTIONS = [
  {
    label: 'Overview',
    items: [
      { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true, adminOnly: true },
    ],
  },
  {
    label: 'People & Access',
    items: [
      { to: '/admin/users', label: 'People', icon: Users, section: 'people' },
      { to: '/admin/roles', label: 'Roles & Permissions', icon: Shield, section: 'roles' },
      { to: '/admin/positions', label: 'Position Types', icon: Briefcase, section: 'people' },
      { to: '/admin/contracts', label: 'Contracts', icon: FileText, section: 'contracts' },
    ],
  },
  {
    // Each domain lists the actionable queue first, then its config.
    label: 'Leave',
    items: [
      { to: '/admin/leave-requests', label: 'Leave Requests', icon: ClipboardList, section: 'leaveRequests' },
      { to: '/admin/balance-adjustments', label: 'Balance Adjustments', icon: SlidersHorizontal, section: 'balances' },
      { to: '/admin/rollover', label: 'Year-End Rollover', icon: RotateCcw, section: 'balances' },
      { to: '/admin/leave-types', label: 'Leave Types', icon: Umbrella, section: 'leaveConfig' },
      { to: '/admin/holidays', label: 'Holidays', icon: CalendarDays, section: 'leaveConfig' },
    ],
  },
  {
    label: 'Expenses',
    items: [
      { to: '/admin/expense-requests', label: 'Expense Requests', icon: ClipboardList, section: 'expenses' },
      { to: '/admin/expense-reports', label: 'Expense Reports', icon: FileSpreadsheet, section: 'expenses' },
      { to: '/admin/expense-recovery', label: 'Receipt Recovery', icon: Wrench, section: 'expenses' },
      { to: '/admin/expense-types', label: 'Expense Types', icon: Receipt, section: 'expenseConfig' },
    ],
  },
  {
    label: 'Travel & Visas',
    items: [
      { to: '/admin/travel', label: 'Travel Requests', icon: Plane, section: 'travel' },
      { to: '/admin/visas', label: 'Visa Requests', icon: Stamp, section: 'visas' },
    ],
  },
  {
    label: 'Documents',
    items: [
      { to: '/admin/documents', label: 'Documents', icon: FolderOpen, section: 'documents' },
      { to: '/admin/document-types', label: 'Document Types', icon: Tag, section: 'documents' },
    ],
  },
  {
    label: 'Reports & Audit',
    items: [
      // Finance/expense reporting lives here (section 'expenses', which the
      // Finance role has) alongside leave Reports + Audit ('reports' section),
      // so all reporting is in one obvious place.
      { to: '/admin/expense-monthly-report', label: 'Monthly Expense Report', icon: CalendarRange, section: 'expenses' },
      { to: '/admin/reports', label: 'Leave Reports', icon: BarChart3, section: 'reports' },
      { to: '/admin/audit', label: 'Audit Log', icon: FileSearch, section: 'reports' },
    ],
  },
  {
    label: 'Configuration',
    items: [
      { to: '/admin/chains', label: 'Approval Chains', icon: GitBranch, section: 'workflow' },
      { to: '/admin/templates', label: 'Email Templates', icon: Mail, section: 'settings' },
      { to: '/admin/settings', label: 'Settings', icon: Settings, section: 'settings' },
    ],
  },
  {
    label: null,
    items: [
      { to: '/admin/help', label: 'Help', icon: HelpCircle },
    ],
  },
];

function AdminContent() {
  const sidebar = useSidebar();
  const collapsed = sidebar?.collapsed;

  return (
    <main
      className={cn(
        'min-h-screen transition-all duration-200 bg-background',
        'pt-16 lg:pt-0',
        collapsed ? 'lg:ml-[60px]' : 'lg:ml-[240px]'
      )}
    >
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <Outlet />
      </div>
    </main>
  );
}

export default function AdminLayout() {
  const { accounts, instance } = useMsal();
  const account = accounts[0];
  const { isAdmin, can } = useCurrentUser();

  // Section-based sidebar. Admins see everything; everyone else sees an item
  // only if their role grants its `section`. Unmapped items (Help) show for
  // any admin-panel user; `adminOnly` items (Dashboard) are admins only.
  const navSections = isAdmin
    ? NAV_SECTIONS
    : NAV_SECTIONS
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => {
            if (item.adminOnly) return false;
            if (!item.section) return true;
            return can(item.section);
          }),
        }))
        .filter((section) => section.items.length > 0);

  return (
    <div className="min-h-screen bg-background">
      <Sidebar
        navSections={navSections}
        brandName="KSA ProHR"
        storageKey="admin-sidebar-collapsed"
        showWorkspaceSwitcher={true}
        isAdmin={true}
        aboveUserSlot={({ collapsed }) => <WhatsNew collapsed={collapsed} />}
        userInfo={{
          name: account?.name || 'Admin',
          email: account?.username,
          initials: account?.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'A',
        }}
        onLogout={() => instance.logoutRedirect()}
      />
      <AdminContent />
    </div>
  );
}
